"use client";

import { useEffect, useState } from "react";

export interface SimilarTicketMatch {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Debounced lookup against `/api/support/similar` — surfaces the
 * submitter's own recent overlapping tickets so the new-ticket form
 * can warn against accidental duplicates.
 *
 * Returns an empty array until the subject is at least 4 characters,
 * to avoid noisy queries while the submitter is still typing.
 */
export function useSimilarTickets({
  subject,
  category,
  endpoint = "/api/support/similar",
  debounceMs = 400,
  enabled = true,
}: {
  subject: string;
  /** Optional — the matcher works on subject overlap when omitted. */
  category?: string | null;
  endpoint?: string;
  debounceMs?: number;
  enabled?: boolean;
}): SimilarTicketMatch[] {
  const [matches, setMatches] = useState<SimilarTicketMatch[]>([]);
  useEffect(() => {
    if (!enabled || subject.trim().length < 4) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      const sp = new URLSearchParams({ subject: subject.trim() });
      if (category) sp.set("category", category);
      fetch(`${endpoint}?${sp.toString()}`)
        .then((r) => (r.ok ? r.json() : { tickets: [] }))
        .then((d: { tickets?: SimilarTicketMatch[] }) => {
          if (cancelled) return;
          setMatches(d.tickets ?? []);
        })
        .catch(() => !cancelled && setMatches([]));
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [subject, category, endpoint, debounceMs, enabled]);
  return matches;
}

const STATUS_TONE: Record<string, string> = {
  OPEN: "bg-blue-100 text-blue-800",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
  WAITING_ON_USER: "bg-purple-100 text-purple-800",
  RESOLVED: "bg-emerald-100 text-emerald-800",
};

/**
 * Soft-warning panel surfaced under the subject input on the
 * new-ticket forms. Each match is clickable (target=blank so the
 * in-progress draft isn't lost) and shows ticket #, subject, status,
 * and last-updated date.
 *
 * `basePath` is the in-app prefix for ticket detail URLs — e.g.
 * "/dashboard/support/tickets" for the client/dashboard-style apps,
 * "/support/tickets" for the therapist/clinician-style apps.
 */
export function SimilarTicketsWarning({
  matches,
  basePath,
}: {
  matches: SimilarTicketMatch[];
  basePath: string;
}) {
  if (matches.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
      <div className="flex items-start gap-2.5">
        <span aria-hidden className="mt-0.5 text-base">
          ⚠️
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-amber-950">
            You may have already opened{" "}
            {matches.length === 1 ? "a similar ticket" : "similar tickets"}
          </div>
          <p className="mt-0.5 text-xs text-amber-800/80">
            Check whether{" "}
            {matches.length === 1 ? "this thread" : "one of these threads"} is
            what you wanted — if so, reply there instead so we don't split the
            conversation.
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {matches.map((t) => (
              <li key={t.id}>
                <a
                  href={`${basePath}/${t.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2 shadow-sm transition hover:border-amber-300 hover:shadow"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[10px] font-medium text-gray-500">
                      <span className="font-mono">#{t.ticketNumber}</span>
                      <span>·</span>
                      <span>
                        Updated {new Date(t.updatedAt).toLocaleDateString('en-US', { timeZone: 'America/Toronto' })}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-sm font-medium text-gray-900 group-hover:text-amber-900">
                      {t.subject}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      STATUS_TONE[t.status] ?? "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {t.status.replace(/_/g, " ")}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-2.5 text-[11px] text-amber-700/80">
            If your new request is different, keep filling this out — we'll
            create a separate ticket.
          </p>
        </div>
      </div>
    </div>
  );
}
